import { useFrame, useThree } from "@react-three/fiber";
import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import createInputEvents from "simple-input-events";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass";

/**
 * Make render pass effect for each {element}
 * @param type
 * @param element
 * @returns {*}
 * @constructor
 */
function CustomRenderPassEffect({ type, element }) {
  const { scene, gl, size, camera } = useThree();
  // const aspect = useMemo(() => new THREE.Vector2(512, 512), []);
  const composer = useRef();
  let customPass = useRef();
  let renderEffectType = useRef();
  let uMouse = useRef(new THREE.Vector2(0, 0));
  let followMouse = useRef(new THREE.Vector2());
  let prevMouse = useRef(new THREE.Vector2());
  let time = useRef(0);
  let speed = useRef(0);
  let targetSpeed = useRef(0);

  // Get DOM component to make effect
  useEffect(() => {
    const ele = "#" + element;
    const projectImg = document.querySelector(ele);
    const event = createInputEvents(projectImg);

    event.on("move", ({ position, event, inside, dragging }) => {
      uMouse.current.x = position[0] / projectImg.clientWidth;
      uMouse.current.y = 1 - position[1] / projectImg.clientHeight;
    });
  }, [element]);

  const getSpeed = () => {
    speed.current = Math.sqrt(
      (prevMouse.current.x - uMouse.current.x) ** 2 +
        (prevMouse.current.y - uMouse.current.y) ** 2
    );

    targetSpeed.current -= 0.1 * (targetSpeed.current - speed.current);
    followMouse.current.x -= 0.1 * (followMouse.current.x - uMouse.current.x);
    followMouse.current.y -= 0.1 * (followMouse.current.y - uMouse.current.y);

    prevMouse.current.x = uMouse.current.x;
    prevMouse.current.y = uMouse.current.y;
  };

  // Make shader pass with effects
  useEffect(() => {
    switch (type) {
      case "colorful":
        const colorfulEffect = {
          // uniforms: {
          //   'tDiffuse': {value: null},
          //   'resolution': {
          //     value: new THREE.Vector2(1., window.innerHeight / window.innerWidth),
          //   },
          //   'uMouse': {value: new THREE.Vector2(-10, -10)},
          //   'uVelo': {value: 0},
          //   'time': { value: 0 }
          // },
          uniforms: {
            tDiffuse: { value: null },
            distort: { value: 0 },
            resolution: {
              value: new THREE.Vector2(
                1,
                window.innerHeight / window.innerWidth
              ),
            },
            uMouse: { value: new THREE.Vector2(-10, -10) },
            uVelo: { value: 0 },
            uScale: { value: 0 },
            time: { value: 0 },
          },
          vertexShader: `
            uniform float time;
            uniform float progress;
            uniform vec2 resolution;
            varying vec2 vUv;
            uniform sampler2D texture1;
            
            const float pi = 3.1415925;
            
            void main() {
              vUv = uv;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0 );
            }
          `,
          fragmentShader: `
            uniform float time;
            uniform float progress;
            uniform sampler2D tDiffuse;
            uniform vec2 resolution;
            varying vec2 vUv;
            uniform vec2 uMouse;
            uniform float uVelo;
  
          float circle(vec2 uv, vec2 disc_center, float disc_radius, float border_size) {
            uv -= disc_center;
            uv*=resolution;
            float dist = sqrt(dot(uv, uv));
            return smoothstep(disc_radius+border_size, disc_radius-border_size, dist);
          }
          void main()  {
              vec2 newUV = vUv;
              float c = circle(vUv, uMouse, 0.0, 0.2);
              float r = texture2D(tDiffuse, newUV.xy += c * (0.1 * .5)).x;
              float g = texture2D(tDiffuse, newUV.xy += c * (0.1 * .525)).y;
              float b = texture2D(tDiffuse, newUV.xy += c * (0.1 * .55)).z;
              vec4 color = vec4(r, g, b, 1.);
  
              gl_FragColor = color;
          }`,
        };
        renderEffectType.current = colorfulEffect;
        break;
      case "zoom":
        const zoomEffect = {
          // uniforms: {
          //   'tDiffuse': {value: null},
          //   'resolution': {
          //     value: new THREE.Vector2(1., window.innerHeight / window.innerWidth),
          //   },
          //   'uMouse': {value: new THREE.Vector2(-10, -10)},
          //   'uVelo': {value: 0.05},
          //   'time': { value: 0 }
          // },
          uniforms: {
            tDiffuse: { value: null },
            distort: { value: 0 },
            resolution: {
              value: new THREE.Vector2(
                1,
                window.innerHeight / window.innerWidth
              ),
            },
            uMouse: { value: new THREE.Vector2(-10, -10) },
            uVelo: { value: 0 },
            uScale: { value: 0 },
            time: { value: 0 },
          },
          vertexShader: `
            uniform float time;
            uniform float progress;
            uniform vec2 resolution;
            varying vec2 vUv;
            uniform sampler2D texture1;
            
            const float pi = 3.1415925;
            
            void main() {
              vUv = uv;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0 );
            }
          `,
          fragmentShader: `
            uniform float time;
            uniform sampler2D tDiffuse;
            uniform vec2 resolution;
            varying vec2 vUv;
            uniform vec2 uMouse;
            uniform float uVelo;
            
            float circle(vec2 uv, vec2 disc_center, float disc_radius, float border_size) {
              uv -= disc_center;
              uv*=resolution;
              float dist = sqrt(dot(uv, uv));
              return smoothstep(disc_radius+border_size, disc_radius-border_size, dist);
            }
            void main()  {
                vec2 newUV = vUv;
                vec4 color = vec4(1.,0.,0.,1.);
                
                float c = circle(newUV, uMouse, 0.0, 0.1+uVelo*2.)*40.*uVelo;
                vec2 offsetVector = normalize(uMouse - vUv);
                vec2 warpedUV = mix(vUv, uMouse, c * 0.99); //power
                color = texture2D(tDiffuse,warpedUV) + texture2D(tDiffuse,warpedUV)*vec4(vec3(c),1.);
    
                gl_FragColor = color;
            }
          `,
        };
        renderEffectType.current = zoomEffect;
        break;
      case "random":
        const randomEffect = {
          // uniforms: {
          //   'tDiffuse': {value: null},
          //   'resolution': {
          //     value: new THREE.Vector2(1., window.innerHeight / window.innerWidth),
          //   },
          //   'uMouse': {value: new THREE.Vector2(-10, -10)},
          //   'uVelo': {value: 0.05},
          //   'time': { value: 0 }
          // },
          uniforms: {
            tDiffuse: { value: null },
            distort: { value: 0 },
            resolution: {
              value: new THREE.Vector2(
                1,
                window.innerHeight / window.innerWidth
              ),
            },
            uMouse: { value: new THREE.Vector2(-10, -10) },
            uVelo: { value: 0 },
            uScale: { value: 0 },
            time: { value: 0 },
          },
          vertexShader: `
            uniform float time;
            uniform float progress;
            uniform vec2 resolution;
            varying vec2 vUv;
            uniform sampler2D texture1;
            
            const float pi = 3.1415925;
            
            void main() {
              vUv = uv;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0 );
            }
          `,
          fragmentShader: `
            uniform float time;
            uniform float progress;
            uniform sampler2D tDiffuse;
            uniform vec2 resolution;
            varying vec2 vUv;
            uniform vec2 uMouse;
            uniform float uVelo;
            
            float circle(vec2 uv, vec2 disc_center, float disc_radius, float border_size) {
              uv -= disc_center;
              uv*=resolution;
              float dist = sqrt(dot(uv, uv));
              return smoothstep(disc_radius+border_size, disc_radius-border_size, dist);
            }
            
            float map(float value, float min1, float max1, float min2, float max2) {
              return min2 + (value - min1) * (max2 - min2) / (max1 - min1);
            }
    
            float remap(float value, float inMin, float inMax, float outMin, float outMax) {
              return outMin + (outMax - outMin) * (value - inMin) / (inMax - inMin);
            }
    
            float hash12(vec2 p) {
              float h = dot(p,vec2(127.1,311.7));
              return fract(sin(h)*43758.5453123);
            }
    
            // #define HASHSCALE3 vec3(.1031, .1030, .0973)
            vec2 hash2d(vec2 p)
            {
                vec3 p3 = fract(vec3(p.xyx) * vec3(.1031, .1030, .0973));
                p3 += dot(p3, p3.yzx+19.19);
                return fract((p3.xx+p3.yz)*p3.zy);
            }
    
            void main()  {
                vec2 newUV = vUv;
                vec4 color = vec4(1.,0.,0.,1.);
                
                float hash = hash12(vUv*10.);
                
                float c = circle(newUV, uMouse, 0.0, 0.1+uVelo*0.01)*10.*uVelo;
                vec2 offsetVector = normalize(uMouse - vUv);
    
                vec2 warpedUV = vUv + vec2(hash - 0.5)*c; //power
                color = texture2D(tDiffuse,warpedUV) + texture2D(tDiffuse,warpedUV)*vec4(vec3(c),1.);
    
                gl_FragColor = color;
            }
          `,
        };
        renderEffectType.current = randomEffect;
        break;
      case "default":
        break;
    }

    customPass.current = new ShaderPass(renderEffectType.current);
    customPass.current.renderToScreen = true;
    composer.current.addPass(customPass.current);
  }, [type]);

  // Set size for composer to render
  useEffect(() => {
    void composer.current.setSize(size.width, size.height);
  }, [size]);

  //
  useFrame(({ clock }) => {
    getSpeed();

    time.current += 0.05;
    // customPass.current.uniforms.time.value = time.current;
    // customPass.current.uniforms.uMouse.value = uMouse.current;

    customPass.current.uniforms.time.value = time.current;
    customPass.current.uniforms.uMouse.value = followMouse.current;
    customPass.current.uniforms.uVelo.value = Math.min(
      targetSpeed.current,
      0.05
    );
    targetSpeed.current *= 0.999;

    composer.current.render();
  }, 1);

  //
  return (
    <effectComposer ref={composer} args={[gl]}>
      <renderPass attachArray="passes" scene={scene} camera={camera} />
    </effectComposer>
  );
}

export default CustomRenderPassEffect;
